import {
    type ApiContainerType,
    type ApiRequisitionContainerAction,
    type ApiService,
    EnumContainerOrderAction,
} from '@Model';
import { type ContainerTypeDetailsByTypeId } from '@Modules/Orders/Editing/Containers';
import {
    type AsyncStateUpdate,
    type AsyncStateUpdateRequest,
    type Awaitable,
    type ILinkInputProperties,
    isLinked,
    type LinkableField,
    useGuiBinder,
    useLinkFieldApi,
    useLinkProperty,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';
import {
    type ContainerPosition,
    type ContainerPositionOrigin,
    type ContainerStockLine,
    lineOf,
    recordedCountOf,
} from './containerOrder';
import { usePortalLinkPickerProps } from './portalLinkPicker';

/**
 * What the records a requisition links are called on screen. In the portal that is the name the
 * operator picked for the customer; everywhere else the name the link itself carries.
 */
export interface LinkNaming {
    containerTypeOf: (containerType: LinkableField | undefined) => string;
    materialOf: (service: LinkableField | undefined) => string;
}

/** What the steps work with for one container action of the order. */
export interface ContainerSlot {
    /** The container the action stands for, undefined while no type has been named. */
    position: ContainerPosition | undefined;
    /** The type the action asks for, as a link on the stored container action. */
    containerTypeField: ILinkInputProperties<ApiContainerType>;
    /** What is in the container, as a link on the stored container action. */
    contentField: ILinkInputProperties<ApiService>;
    /** Changes how many containers of the chosen type the action asks for. */
    changeCount: (count: number) => void;
    /** The number the customer gives a container we have no record of. */
    setContainerNumber: (containerNumber: string) => void;
    /** Where the customer says that container stands. */
    setLocationHint: (locationHint: string) => void;
}

interface ContainerSlotOptions {
    /** The stored container action the slot is about, undefined while it is still being minted. */
    row: ApiRequisitionContainerAction | undefined;
    requisitionPath: string;
    naming: LinkNaming;
    stockLines: readonly ContainerStockLine[];
    /** How the container types of the order are managed, as the shared lookup answers it. */
    management: ContainerTypeDetailsByTypeId;
    canEdit: boolean;
    requestUpdateSubObject: (
        updater: AsyncStateUpdateRequest<ApiRequisitionContainerAction>,
        id: string,
    ) => Awaitable<boolean>;
}

/** The placeholder path the link hooks carry while no container action exists to link on. */
const NO_ACTION = 'none';

/**
 * What the link fields bind to while the row they belong to is still being minted.
 *
 * A link field reads its `LinkableField` during render and throws without one, so the fields stay
 * mounted on a blank stand-in rather than disappearing for the render or two it takes the server
 * to answer. They are locked in that state, because there is nothing yet to write to.
 */
const blankLink = () => ({ foreign_key: undefined, display_string: '' });
const BLANK_ACTION = {
    id: NO_ACTION,
    container_type: blankLink(),
    container: blankLink(),
    content: blankLink(),
} as unknown as ApiRequisitionContainerAction;

/** One container action of the order, with the fields and writes that belong to it. */
// @ai-generated 2026-10-09T08:04:13+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useContainerSlot({
    row,
    requisitionPath,
    naming,
    stockLines,
    management,
    canEdit,
    requestUpdateSubObject,
}: ContainerSlotOptions): ContainerSlot {
    const rowId = row?.id;
    const actionPath = `${requisitionPath}/container_actions/${rowId ?? NO_ACTION}`;

    const linkContainerType = useLinkFieldApi<ApiRequisitionContainerAction, ApiContainerType>(
        actionPath,
        'container_type',
    );
    const linkContent = useLinkFieldApi<ApiRequisitionContainerAction, ApiService>(actionPath, 'content');

    const position = useMemo(
        () => (row ? toPosition(row, stockLines, management, naming) : undefined),
        [row, stockLines, management, naming],
    );

    /** Every write to the stored container action, addressed at the row of this slot. */
    const requestUpdateRow = useCallback<AsyncStateUpdate<ApiRequisitionContainerAction>>(
        (updater: AsyncStateUpdateRequest<ApiRequisitionContainerAction>) =>
            rowId ? requestUpdateSubObject(updater, rowId) : Promise.resolve(false),
        [rowId, requestUpdateSubObject],
    );

    const rowBinder = useGuiBinder<ApiRequisitionContainerAction>({
        data: row ?? BLANK_ACTION,
        meta: undefined,
        canEdit: canEdit && row !== undefined,
        requestUpdateState: requestUpdateRow,
    });

    const containerTypePicker = usePortalLinkPickerProps<ApiContainerType>();
    const contentPicker = usePortalLinkPickerProps<ApiService>();

    const boundContainerType = useLinkProperty(rowBinder, linkContainerType);
    const containerTypeName = naming.containerTypeOf(row?.container_type);
    const containerTypeField = useMemo<ILinkInputProperties<ApiContainerType>>(
        () => ({ ...boundContainerType, ...containerTypePicker, value: containerTypeName }),
        [boundContainerType, containerTypePicker, containerTypeName],
    );

    const boundContent = useLinkProperty(rowBinder, linkContent);
    const materialName = naming.materialOf(row?.content);
    const contentField = useMemo<ILinkInputProperties<ApiService>>(
        () => ({ ...boundContent, ...contentPicker, value: materialName }),
        [boundContent, contentPicker, materialName],
    );

    /** Applies a change to the stored container action and waits for it to land. */
    const updateRow = useCallback(
        (change: (current: ApiRequisitionContainerAction) => ApiRequisitionContainerAction) =>
            requestUpdateRow(async (current, save) => save(change(current))),
        [requestUpdateRow],
    );

    const changeCount = useCallback(
        (count: number) => {
            void updateRow((stored) => ({ ...stored, count }));
        },
        [updateRow],
    );

    const setContainerNumber = useCallback(
        (containerNumber: string) => {
            void updateRow((stored) => ({
                ...stored,
                container: { ...stored.container, display_string: containerNumber },
            }));
        },
        [updateRow],
    );

    const setLocationHint = useCallback(
        (locationHint: string) => {
            void updateRow((stored) => ({ ...stored, remarks: locationHint }));
        },
        [updateRow],
    );

    return useMemo(
        () => ({ position, containerTypeField, contentField, changeCount, setContainerNumber, setLocationHint }),
        [position, containerTypeField, contentField, changeCount, setContainerNumber, setLocationHint],
    );
}

/**
 * The stored container action as the steps and the summary read it.
 *
 * A row without a container type is an action the customer has chosen but not answered yet, which
 * is no container at all.
 */
export function toPosition(
    row: ApiRequisitionContainerAction,
    stockLines: readonly ContainerStockLine[],
    management: ContainerTypeDetailsByTypeId,
    naming: LinkNaming,
): ContainerPosition | undefined {
    if (!isLinked(row.container_type)) {
        return undefined;
    }

    const containerId = isLinked(row.container) ? row.container.foreign_key : undefined;
    const containerTypeId = row.container_type.foreign_key ?? '';
    const line = lineOf(stockLines, { containerId, containerTypeId });
    const origin = originOf(row.action, line);
    const content = isLinked(row.content) ? row.content : undefined;

    return {
        origin,
        containerTypeId,
        containerTypeLabel: naming.containerTypeOf(row.container_type),
        usesIndividualManagement:
            line?.usesIndividualManagement ?? management.get(containerTypeId)?.usesIndividualManagement,
        action: row.action,
        containerId,
        containerNumber: row.container.display_string,
        locationHint: row.remarks,
        count: row.count,
        stockCount: origin === 'stock' ? recordedCountOf(stockLines, { containerId, containerTypeId }) : undefined,
        serviceId: content?.foreign_key,
        serviceLabel: naming.materialOf(content),
    };
}

/**
 * Where the position came from.
 *
 * The origin is not stored: a placement is one by its action, and any other container is one of
 * ours exactly when the stock still shows the line it was picked from. Without a readable stock
 * every such container reads as described — which is also the only thing the step can offer in
 * that state.
 */
function originOf(action: EnumContainerOrderAction, line: ContainerStockLine | undefined): ContainerPositionOrigin {
    if (action === EnumContainerOrderAction.Place) {
        return 'placement';
    }

    return line ? 'stock' : 'described';
}
